import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { Modal, notification } from 'antd';
import { Flex } from '@/components';
import { ForceChangePasswordModal } from '@/components/modals';
import { storage } from '@/utils/storage';
import { envConfig } from '@/configs';
import { Routes } from '@/routes';
import i18next from 'i18next';
import * as sentry from '@/utils';

const http = axios.create({
  validateStatus: (status) => status >= 200 && status <= 302,
  transformResponse: [].concat(
    axios.defaults.transformResponse,
    (data: any) => data
  ),
});

let isModalOpen = false;

const ERR_IGNORE_KEY = [
  'USER_IMAGE_NOT_FOUND',
  'STUDENT_GROUP_NOT_FOUND',
  'STUDENT_GROUP_NO_ROOM_REGISTERED',
  'DAILY_SELL_PROGRAM_NO_PROGRAM_FOR_TODAY',
  'SELF_HAS_NO_PROGRAM',
  'STUDENT_GROUP_DORMITORY_ROOM_NOT_FOUND',
  'EXCEED_MONTHLY_VALID_FORGOTTEN_COUNT',
  'EXCEED_WEEKLY_VALID_FORGOTTEN_COUNT',
  'STUDENT_GROUP_EDIT_NO_ROOM_REMAINS',
  'VALID_ENTRANCE_DORMITORY_NOT_FOUND',
  'STUDENT_GROUP_USER_IS_NOT_A_STUDENT',
  'DAILY_SELL_PROGRAM_DAILY_RESERVE_COUNT_LIMIT_VIOLATION',
  ['RESERVE_MAX_RESERVE_COUNT_CROSS', 'مورد انتخابی تا حداکثر سقف ممکن، توسط کاربران رزرو شده است!'],
];

const ERR_IGNORE_GET_URL = [
  '/rest/users/me/messages/inbox',
  '/rest/users/me/dormitories',
  '/rest/users/me/dormitory-requests',
  '/rest/users/me/surveys',
  '/rest/users/me/eskans/cardexes'
];

const ERR_IGNORE_URL_REGEX = [
  /.*\/images\/verify/i
];

function canIgnoreWithRegex(key: string) {
  for (const item of ERR_IGNORE_URL_REGEX) {
    if (item.test(key)) {
      return true;
    }
  }
  return false;
}

export const SAMIN_KEY = 'e461770de1c4e1535f268744630c8ff7915e86ba';
export const SAMIN_WRITE_KEY = 'b2db19f014c3a3bfe9205f92da3ab6ec843f3aa9';

export function isExeedForgottenCode(e: AxiosError<any>) {
  return [
    'EXCEED_WEEKLY_VALID_FORGOTTEN_COUNT',
    'EXCEED_MONTHLY_VALID_FORGOTTEN_COUNT'
  ].includes(e?.response?.data?.messageResource);
}

export const showError = (error: string | any) => {
  let content = '';
  if (typeof error === 'string') {
    content = error;
  }
  else {
    content = error.response?.data?.messageFa ?? error.response?.data?.message;
  }
  if (!isModalOpen) {
    isModalOpen = true;
    notification.error({
      key: 'http-error',
      style: { direction: 'rtl', fontFamily: 'var(--app-font-sans)' },
      className: 'cursor-pointer',
      message: (
        <Flex direction="column">
          <span className="font-bold text-base">
            {i18next.t('common:errors.exec-action')}
          </span>
          <span className="text-sm">{content}</span>
        </Flex>
      ),
      onClick: () => {
        isModalOpen = false;
        notification.destroy('http-error');
      },
      // maxCount: 2,
      placement: 'top',
      closeIcon: <></>,
      onClose: () => isModalOpen = false,
    });
  }
};

export const showChangePasswordModal = () => {
  if (!isModalOpen) {
    isModalOpen = true;
    Modal.warning({
      width: 'min(90vw, 500px)',
      icon: <></>,
      direction: 'rtl',
      centered: true,
      okCancel: false,
      keyboard: false,
      open: true,
      onOk: () => { },
      closable: false,
      okButtonProps: { style: { display: 'none' } },
      content: (<ForceChangePasswordModal />),
      onCancel: () => { }
    });
  }
};

http.interceptors.response.use(response => {
  return response;
}, async (error: AxiosError<any>) => {
  const universityURL = getUniversityUrl();
  const originalRequest = error.config;
  const status = error?.response?.status;
  const refreshToken = storage.get('REFRESH_TOKEN', '');

  if (error.config.url === '/oauth/token') {
    sentry.authError(error);
  }

  if (status === 401 && refreshToken != null && error.config.url != '/oauth/token') {
    console.log('Try to use refresh token');
    const data = new URLSearchParams();
    data.append('grant_type', 'refresh_token');
    data.append('refresh_token', refreshToken as string);
    try {
      const refreshResponse = await axios.post(`${universityURL}/oauth/token`, data, {
        headers: {
          'Authorization': 'Basic c2FtYWQtbW9iaWxlOnNhbWFkLW1vYmlsZS1zZWNyZXQ=',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
        }
      });
      storage.set('TOKEN', refreshResponse?.data?.access_token);
      const newConfig = { ...originalRequest };
      newConfig.headers['Authorization'] = `Bearer ${refreshResponse?.data?.access_token}`;
      return await http(newConfig);
    }
    catch (e) {
      console.error('Error in regenerate access token');
      storage.removeMultiple(['TOKEN', 'REFRESH_TOKEN']);
      if (window.location.pathname != Routes.Login) {
        window.location.href = Routes.Login;
      }
      return Promise.reject(e);
    }

  }

  if (error?.response?.status === 403 && error?.response?.data?.messageResource === 'USER_FORCE_CHANGE_PASSWORD') {
    return showChangePasswordModal();
  }
  else if (status >= 400 && error?.response?.data?.message) {
    if (error?.response?.data?.messageResource) {

      for (const item of ERR_IGNORE_KEY) {
        if (Array.isArray(item)) {
          if (item[0] === error?.response?.data?.messageResource && item[1] != null) {
            showError(item[1]);
          }
        }
        if (item === error?.response?.data?.messageResource) {
          return Promise.reject(error);
        }
      }

      if (ERR_IGNORE_KEY.includes(error?.response?.data?.messageResource)) {
        return Promise.reject(error);
      }

      if (originalRequest.method === 'get' && ERR_IGNORE_GET_URL.includes(error.config.url)) {
        return Promise.reject(error);
      }

      if (canIgnoreWithRegex(error.config.url)) {
        return Promise.reject(error);
      }

      // Why this should handle on client side :/ ?
      if (error?.response?.data?.message?.includes('exists but is a directory')) {
        return Promise.reject(error);
      }
      showError(error);
    }
  }
  else {
    // console.log('err', status);
  }
  return Promise.reject(error);
});

export function getUniversityUrl() {
  const uni = storage.get('UNIVERSITY', null) as UniversityEntity;
  return uni?.universityURL ?? null;
}

export function getUniversityKey() {
  const uni = storage.get('UNIVERSITY', null) as UniversityEntity;
  return uni?.unikey ?? null;
}

export function getFaceEnrollmentUrl() {
  const uni = storage.get('UNIVERSITY', null) as UniversityEntity;
  return uni?.samad?.faceEnrollmentURL ?? null;
}

export function getHelpDeskUrl() {
  const uni = storage.get('UNIVERSITY', null) as UniversityEntity;
  if (!uni) return null;
  return uni?.samad?.help_desk;
}

export function getFeedbackURL() {
  if (['on-promise', 'on-premise'].includes(envConfig.serveMode)) {
    return envConfig.onPremise?.core?.feedbackUrl;
  }
  return 'https://api.samad.app';
}

export function defaultConfig(baseURL: string, options = { auth: true, headers: {} }): AxiosRequestConfig {
  const token = storage.get('TOKEN');
  return {
    baseURL,
    timeout: 70000,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      ...(options.auth ? { authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {})
    }
  };
}

export function getBlobConfig(baseURL: string) {
  const token = storage.get('TOKEN');
  return {
    baseURL,
    timeout: 70000,
    headers: {
      'Cache-Control': 'no-cache',
      'Authorization': `Bearer ${token}`
    }
  };
}

export { http };
export default http;
